import ListCard from '@Components/ListCard/ListCard';
import usePagination from '@Hooks/usePagination';
import ReportService from '@Services/Report.services.ts';
import { Report } from '@Types/Report.types';
import { PaginatedResponse } from '@Types/Response.types';
import { Table } from '@Types/Table.types';
import { PAGE_SIZE, PAGINATED_DEFAULT, PATHS, TABLE_STRUCTURES, TABLE_VALUES } from '@Utils/constants';
import { toggleColumn, transformColumnStructure } from '@Utils/reports.utils';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, MultiSelectDropdown, Pagination, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import styles from './ReportDetails.module.scss';

const ReportDetails = () => {
  const [tableStructure, setTableStructure] = useState<Table[]>([]);

  const [tableValues, setTableValues] = useState<Table[]>([]);

  const [details, setDetails] = useState<PaginatedResponse<Report>>({
    ...PAGINATED_DEFAULT,
  });

  const [allOptions, setAllOptions] = useState(transformColumnStructure(tableValues));
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [defaultOptions, setDefaultOptions] = useState(transformColumnStructure(tableStructure));
  const [mainColumn, setMainColumn] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [report, setReport] = useState<Report>();
  const [selectedOptions, setSelectedOptions] = useState(defaultOptions || []);
  const [visibleColumns, setVisibleColumns] = useState(tableStructure);
  const { currentData, jump } = usePagination(details.docs || [], PAGE_SIZE, undefined);
  const { id } = useParams();
  const assetType = 'ASSET';
  const block = 'report-details';
  const navigate = useNavigate();

  useTopHat({
    activeNav: 'nav-reports',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      {
        title: 'Reports',
        href: `${PATHS.REPORTS}?page=${1}`,
        id: 'Reports',
      },
      {
        title: id || 'Reports',
        href: `${PATHS.REPORTS}/${id}`,
        id: id || 'Reports',
      },
    ],
  });

  const handleToggleColumn = (columnKey: string) => {
    setVisibleColumns((prevColumns) => toggleColumn(prevColumns, columnKey, Object.values(TABLE_VALUES.ASSET)));
  };

  const removeFilter = (option: { label: string; value: string }) => {
    if (option.value !== mainColumn) {
      handleToggleColumn(option.value);
      setSelectedOptions(selectedOptions.filter((item) => item !== option));
    }
  };

  const setAssetData = () => {
    setTableStructure(TABLE_STRUCTURES.REPORTS_ASSET);
    setTableValues(Object.values(TABLE_VALUES.ASSET));
    setMainColumn('serialNumber');
  };

  const setAssetLogData = () => {
    setTableStructure(TABLE_STRUCTURES.REPORTS_ASSET_LOG);
    setTableValues(Object.values(TABLE_VALUES.REPORT_ASSET_LOG));
    setMainColumn('loggedAt');
  };

  const generateExcel = () => {
    ReportService.getExcel(id).then((response) => {
      const href = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = `${report && report.name}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(href);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    ReportService.getReport(id)
      .then(({ data }) => {
        const reportData = data[0];
        if (!reportData) {
          return;
        }
        if (reportData.generatedDocument?.url) {
          window.open(reportData.generatedDocument.url, '_blank');
          navigate(PATHS.REPORTS);
        } else {
          reportData.data = (reportData.data || []).map((field: { id?: string }) => {
            if (!field.id) {
              // Ensure they have an ID to avoid console errors
              field.id = uuid();
            }
            return field;
          });
          setReport(reportData);
          setDetails((prev) => ({ ...prev, docs: reportData.data }));
          setErrorMessage(undefined);
          if (reportData.reportType === assetType) {
            setAssetData();
          } else {
            setAssetLogData();
          }
        }
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    setVisibleColumns(tableStructure);
    setDefaultOptions(transformColumnStructure(tableStructure));
    setAllOptions(transformColumnStructure(tableValues));
  }, [tableStructure]);

  useEffect(() => {
    setSelectedOptions(defaultOptions);
  }, [defaultOptions]);

  return (
    <div className='grid'>
      <div className='col-12'>
        <SectionCard>
          <h2>Report Info</h2>
          <div className={`grid ${styles[`${block}__grid`]}`}>
            <div className='col-md-10'>
              <MultiSelectDropdown
                options={allOptions}
                values={selectedOptions}
                selectOption={(selected) => {
                  handleToggleColumn(selected.value);
                  setSelectedOptions([...selectedOptions, selected]);
                }}
                removeOption={(selected) => removeFilter(selected)}
                fullWidth
              />
            </div>
            <div className={`col-md-2 ${styles[`${block}__button-container`]}`}>
              <Button
                className={`button-with-icon ${styles[`${block}__button`]}`}
                type='primary'
                onClick={generateExcel}
                fullWidth>
                {Icons.Download} Download
              </Button>
            </div>
          </div>
        </SectionCard>
        <ListCard<Report>
          title={(report && report.name) || 'Report Info'}
          structure={visibleColumns}
          data={currentData}
          loading={isLoading}
          detailsPath={PATHS.ASSETS}
          error={errorMessage}
          classname={styles[`${block}__table`]}
        />
        <div className='col-12 content-middle content-right-md'>
          <Pagination
            count={details.docs.length}
            perPage={PAGE_SIZE}
            currentPage={currentPage - 1}
            onChange={(page) => {
              setCurrentPage(page + 1);
              jump(page + 1);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportDetails;
